import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Camera, Images, Mic } from "lucide-react";
import { GlassSheet } from "@/components/filters/GlassSheet";
import { useIsTouchDevice } from "@/hooks/use-touch-device";
import { markFileCaptureStart, clearFileCaptureMark } from "@/lib/fileCaptureGate";

export type ReportMode = "photo" | "voice";

/** Tope compartido con el asistente (MAX_PHOTOS de IncidentReportWizard). */
const MAX_REPORT_PHOTOS = 3;

interface ChoiceProps {
  onPick: (mode: ReportMode) => void;
  /** Cuando se pasa, «Foto» dispara la cámara directamente y entrega lo capturado. */
  onPickPhotoFiles?: (files: File[]) => void;
}

/**
 * Las dos tarjetas de «Reportar plaga» (Foto / Voz), SIN carcasa.
 *
 * Están sueltas porque los dos sitios desde los que se reporta ya no comparten
 * ventana: desde la página de Plagas se abre su propia hoja (GlassSheet, abajo),
 * y desde el conmutador de voz se pintan DENTRO de VoiceModalShell, como la
 * pestaña Muestreo — así salen con el mismo selector Asistente / Tarea por voz /
 * Muestreo, la misma cabecera y el mismo alto que las otras dos, en vez de con
 * una cabecera propia y una flecha de atrás.
 */
/**
 * Abrir la cámara desde un toque, y recoger lo capturado.
 *
 * Vive aquí, y no copiado en cada pantalla que reporta una plaga, por UNA razón
 * concreta: el `.click()` sobre el input tiene que colgar del mismo gesto que lo
 * originó —si llega en otro tick, Safari se lo come y no pasa nada—. Es
 * exactamente el detalle que se pierde al copiar y pegar, y cuando se pierde el
 * botón simplemente no hace nada en el iPhone del trabajador.
 *
 * Devuelve `inputs`, que hay que pintar (van ocultos), y `open`, que se llama
 * DIRECTAMENTE desde el `onClick`.
 */
export function usePhotoCapture(onFiles?: (files: File[]) => void) {
  const canTakePhoto = useIsTouchDevice();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const open = () => {
    markFileCaptureStart();
    (canTakePhoto ? cameraInputRef : fileInputRef).current?.click();
  };

  /**
   * La MISMA pantalla, pero por la galería (petición del usuario 31-08-2026).
   *
   * `open` dispara la cámara en cuanto hay dedo, y eso es lo que se quiere: la
   * plaga se fotografía delante de la planta. Pero una foto de hace dos horas no
   * tenía por dónde entrar — el input de archivos ya existía y en un móvil no lo
   * abría nadie. Este es el segundo camino, y va al MISMO sitio: mismo `onFiles`,
   * mismo tope de tres.
   *
   * Sigue colgando del gesto igual que `open`: si el `.click()` llega en otro
   * tick, Safari se lo come y el botón no hace nada.
   */
  const openGallery = () => {
    markFileCaptureStart();
    fileInputRef.current?.click();
  };

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).slice(0, MAX_REPORT_PHOTOS);
    e.target.value = "";
    clearFileCaptureMark();
    if (files.length === 0) return; // cámara cancelada: seguimos donde estábamos
    onFiles?.(files);
  };

  const inputs = (
    <>
      {/* Cámara en aparatos táctiles; diálogo de archivos (hasta 3) en ordenador. */}
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFiles} />
      <input ref={fileInputRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={handleFiles} />
    </>
  );

  return { canTakePhoto, open, openGallery, inputs, maxPhotos: MAX_REPORT_PHOTOS };
}

export function ReportModeChoices({ onPick, onPickPhotoFiles }: ChoiceProps) {
  const { t } = useTranslation();
  const { canTakePhoto, open: openCamera, openGallery, inputs, maxPhotos } = usePhotoCapture(onPickPhotoFiles);

  const handlePhotoTap = () => {
    if (!onPickPhotoFiles) {
      onPick("photo");
      return;
    }
    openCamera();
  };

  return (
    <>
      {inputs}
      {/* Sin la línea «Elige cómo quieres reportarla»: las dos tarjetas ya dicen
          Foto y Voz con todas las letras, y el título de arriba ya pregunta. */}
      {/* Mismo lenguaje que la pestaña «Tarea por voz»: círculo pastel con halo del
          mismo color, icono de línea dentro y la etiqueta debajo — en vez de los dos
          bloques de color a sangre que desentonaban con el resto del conmutador. */}
      {/* Cada modo va en su tarjeta (como antes), pero con el círculo pastel dentro
          en vez del bloque de color a sangre. */}
      <div className="flex-1 min-h-0 flex flex-col gap-3 py-1">
        <button
          onClick={handlePhotoTap}
          className="group flex-1 min-h-0 flex flex-col items-center justify-center gap-2 rounded-3xl border border-[#e07a55] bg-[#ef8f68] shadow-[0_4px_16px_rgba(20,60,100,0.05)] px-4 py-4 transition-transform active:scale-[0.98]"
        >
          <span className="relative w-16 h-16 rounded-full flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(232,93,58,0.45)] bg-white group-hover:brightness-[0.98] transition-all">
            <span className="absolute inset-0 rounded-full ring-[6px] ring-[#e85d3a]/10" />
            <Camera className="relative w-7 h-7 text-[#e85d3a]" strokeWidth={1.9} />
          </span>
          {/* El texto sigue a lo que hace el botón en ESTE aparato: con dedo abre
              la cámara («Foto»), con ratón abre los archivos («Adjuntar foto»).
              Decirle «Foto» a quien está en un ordenador prometía una cámara que
              no hay. */}
          <span className="text-sm font-bold text-white">
            {canTakePhoto
              ? t("worker.incident.photoMode")
              : t("worker.incident.photoModeAttach", { defaultValue: "Adjuntar foto" })}
          </span>
          <span className="text-xs text-white/90 -mt-1">
            {canTakePhoto
              ? t("worker.incident.photoModeHint", { defaultValue: "Haz fotos de la plaga" })
              : t("worker.incident.photoModeAttachHint", { defaultValue: "Elige las fotos de la plaga" })}
          </span>
        </button>
        <button
          onClick={() => onPick("voice")}
          className="group flex-1 min-h-0 flex flex-col items-center justify-center gap-2 rounded-3xl border border-[#e0a83f] bg-[#efbc5c] shadow-[0_4px_16px_rgba(20,60,100,0.05)] px-4 py-4 transition-transform active:scale-[0.98]"
        >
          <span className="relative w-16 h-16 rounded-full flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(232,154,44,0.45)] bg-white group-hover:brightness-[0.98] transition-all">
            <span className="absolute inset-0 rounded-full ring-[6px] ring-[#e89a2c]/10" />
            <Mic className="relative w-7 h-7 text-[#e89a2c]" strokeWidth={1.9} />
          </span>
          <span className="text-sm font-bold text-white">{t("worker.incident.voiceMode")}</span>
          <span className="text-xs text-white/90 -mt-1">
            {t("worker.incident.voiceModeHint", { defaultValue: "Graba una nota de voz" })}
          </span>
        </button>

        {/* GALERÍA, el segundo camino de la foto (petición del usuario
            31-08-2026). La tarjeta de arriba abre la cámara en el acto —que es
            lo normal: se reporta delante de la planta—, así que una foto de
            hace dos horas no tenía por dónde entrar.

            Solo con dedo y solo cuando la foto se recoge aquí: en un ordenador
            la tarjeta de arriba YA abre el diálogo de archivos, y repetirlo
            debajo serían dos botones para lo mismo. Sin `onPickPhotoFiles` esta
            pantalla no captura nada, solo elige modo.

            Discreto a propósito: es la excepción, no el camino principal, y
            robarle sitio a las dos tarjetas cambiaría cuál de las dos cosas
            parece la normal. */}
        {canTakePhoto && !!onPickPhotoFiles && (
          <button
            onClick={openGallery}
            className="shrink-0 flex items-center justify-center gap-2 rounded-2xl border border-[#b6dded] bg-white px-4 py-3 transition-transform active:scale-[0.98]"
          >
            <Images className="w-[18px] h-[18px] shrink-0 text-[#2E84DE]" strokeWidth={1.9} />
            {/* `min-w-0` + `truncate`: en alemán o en árabe esto es largo y no
                puede estirar la tarjeta. */}
            <span className="min-w-0 truncate text-sm font-semibold text-[#13314A]">
              {t("common.capture.chooseFromGallery")}
            </span>
            <span className="shrink-0 text-xs text-muted-foreground">
              · {t("worker.incident.maxPhotos", { max: maxPhotos })}
            </span>
          </button>
        )}
      </div>
    </>
  );
}

/**
 * «Reportar plaga» con su propia hoja — la de la PÁGINA DE PLAGAS.
 *
 * El conmutador de voz ya no pasa por aquí: pinta `ReportModeChoices` dentro de
 * VoiceModalShell para salir con el mismo cromo que Asistente y Tarea por voz.
 */
export function ReportModePicker({
  open,
  onClose,
  onPick,
  onPickPhotoFiles,
  onBack,
  backLabel,
}: ChoiceProps & {
  open: boolean;
  onClose: () => void;
  /** Cuando se pasa, la cabecera enseña la flecha de atrás. */
  onBack?: () => void;
  backLabel?: string;
}) {
  const { t } = useTranslation();

  return (
    <GlassSheet
      open={open}
      onClose={onClose}
      onBack={onBack}
      backLabel={backLabel}
      title={t("worker.incident.reportTitle")}
    >
      <ReportModeChoices onPick={onPick} onPickPhotoFiles={onPickPhotoFiles} />
    </GlassSheet>
  );
}
